import { useState } from "react";
import {
    Popover,
    PopoverHandler,
    PopoverContent,
    Typography,
} from "@material-tailwind/react";
import {
    ExclamationTriangleIcon,
} from "@heroicons/react/24/solid";
import moment from "moment";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const priorityStyles = {
    high: "text-red-600",
    medium: "text-yellow-600",
    low: "text-green-600",
};

export default function OrderDeadlinePopover({ order_data }) {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation('order');

    const { user } = useSelector((state) => state.user);

    const assigned_user =
        order_data?.assigned_details?.find((item) => item.assigned_to_id === user?.id) ||
        order_data?.assigned_details?.[0];

    const getTimeSlotStatus = (toTime) => {
        if (!toTime) return { color: "text-gray-500", text: t('na_status'), delayed: "" };

        const now = moment();
        const end = moment(toTime);
        const isDelayed = now.isAfter(end);

        let delayedText = "";
        if (isDelayed) {
            const days = now.diff(end, 'days');
            const hours = now.diff(end, 'hours') % 24;

            if (days > 0) {
                delayedText = ` (${days} ${days === 1 ? t('day_delayed') : t('days_delayed')})`;
            } else if (hours > 0) {
                delayedText = ` (${hours} ${hours === 1 ? t('hour_delayed') : t('hours_delayed')})`;
            } else {
                delayedText = ` (${t('delayed_status')})`;
            }
        }

        return {
            color: isDelayed ? "text-red-500" : "text-green-500",
            delayed: delayedText
        };
    };

    return (
        <>
            {assigned_user && (
                <Popover open={open} handler={setOpen} placement="bottom-end">
                    {/* Trigger Button */}
                    {/* <PopoverHandler>
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {t('view_deadline_button')}
                        </span>
                    </PopoverHandler> */}

                    {/* Popover Content (Fully Responsive Mobile-First Spacing) */}
                    <PopoverContent className="w-[calc(100vw-32px)] sm:w-96 max-w-[400px] p-4 sm:p-5 rounded-2xl shadow-lg bg-white border border-gray-200 z-[9999]">
                        <div className="space-y-4">

                            {/* Header */}
                            <Typography variant="h6" className="font-bold text-gray-900">
                                {t('order_deadline_details_title')}
                            </Typography>

                            {/* Info Box */}
                            <div className="bg-gray-50 rounded-xl border p-3 sm:p-4 space-y-3">

                                {/* Time Slot Container - seamlessly shifts to vertical stacking on tiny screens */}
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 sm:gap-4">
                                    <span className="text-gray-700 font-medium shrink-0">{t('time_slot_label')}</span>

                                    <div className="text-xs sm:text-sm font-medium sm:text-right break-words max-w-full">
                                        {(user?.roles[0] === 'Sub Admin' || user?.roles[0] === 'Administrator' || user?.roles[0] === 'Finance') ? (
                                            <span className="text-gray-500 block">
                                                {assigned_user?.deadline
                                                    ? `${t('deadline_label')}: ${moment(assigned_user.deadline).format("D MMM | h:mm A")}`
                                                    : t('na_status')}
                                            </span>
                                        ) : (
                                            (() => {
                                                const from = assigned_user?.timeslot?.from;
                                                const to = assigned_user?.timeslot?.to;
                                                const status = getTimeSlotStatus(to);

                                                return (
                                                    <span className={`${status.color} block`}>
                                                        {from && to
                                                            ? `${moment(from).format("D MMM | h:mm A")} - ${moment(to).format("D MMM | h:mm A")}`
                                                            : t('na_status')}
                                                        {status.delayed && <span className="ml-1 inline-block font-semibold">{status.delayed}</span>}
                                                    </span>
                                                );
                                            })()
                                        )}
                                    </div>
                                </div>

                                {/* Priority Row */}
                                <div className="flex justify-between items-center pt-1 border-t border-gray-200/50 sm:border-none">
                                    <span className="text-gray-700 font-medium">{t('priority_label')}</span>
                                    <span className={`flex items-center gap-1.5 font-bold text-xs sm:text-sm ${priorityStyles[assigned_user?.priority]}`}>
                                        {assigned_user?.priority?.charAt(0).toUpperCase() + assigned_user?.priority?.slice(1)}
                                        <ExclamationTriangleIcon className="w-4 h-4 shrink-0" />
                                    </span>
                                </div>
                            </div>

                            {/* Note Container */}
                            {assigned_user?.notes && (
                                <div className="p-3 bg-red-50 rounded-xl border border-red-200 text-xs sm:text-sm text-red-700 leading-relaxed max-h-[120px] overflow-y-auto">
                                    <strong className="font-semibold mr-1">{t('note_label')}:</strong>
                                    {assigned_user?.notes}
                                </div>
                            )}

                        </div>
                    </PopoverContent>
                </Popover>
            )}
        </>
    );
}